import React, { FC, createContext, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import CreateEstimateHeader from './components/CreateEstimateHeader';
import CreateEstimateSidebar from './components/CreateEstimateSidebar';
import EstimateCustomer from './components/EstimateCustomer';
import CreateEstimateDetails from './components/CreateEstimateDetails';
import EstimateNotesAndAttachmentsSection from './components/EstimateNotesAndAttachmentsSection';
import EstimateLineItemSection from './components/EstimateLineItemSection';
import EstimateFormRemindersSection from './components/EstimateFormRemindersSection';
import { CreateEditEstimateNavEnum } from '../constants';
import { useAppDispatch, useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { Loader } from 'components';
import { CreateEstimateContextType } from '../types';
import {
  getDefaultExpiryDate,
  selectEstimateReminders,
  selectExpiryDate,
  selectIsInitialisingEstimateFromAPI,
  selectRemindersEnabled,
  updateBillingAddress,
  updateDefaultRemindersFromSettings,
  updateEstimateCustomer,
  updateEstimateReminders,
  updateExpiryDate,
  updateIsSignatureEnabled,
  updateRemindersEnabled,
  updateServiceAddress
} from '../estimatesSlice';
import { useGetMerchantCustomizationsQuery } from 'containers/settings/api';
import { FeatureToggleState } from 'containers/settings/constants';
import { useLazyGetEstimateByIdQuery } from '../api';
import { updateCustomizations } from 'containers/settings/invoiceSettingsSlice';
import { useLazyGetJobByIdQuery } from 'containers/jobs/api';
import {
  useGetTaxesAndFeesQuery,
  useLazyGetCustomerByIdQuery,
  useLazyGetInvoiceByIdV2Query
} from 'containers/invoices/api';
import { CustomerAddress } from 'containers/customers/customers.model';
import CreateEstimateMoreOptions from './components/CreateEstimateMoreOptions';
import EditReminderModal from 'components/reminder-modal/EditReminderModal';
import { useLazyGetEstimateSettingsQuery } from 'containers/settings/estimate-settings/api';
import { transformSettingApiToComponentReminders } from 'containers/settings/utils';
import { calculateScheduledDate } from '../utils';
import { ReminderConfig } from 'components/reminder-modal/types';
import { ReminderDateTypeLabel } from 'containers/settings/constants';
import DateUtils from 'utils/dateUtils';

export const CreateEstimateContext = createContext<CreateEstimateContextType>({
  selectedEstimateTab: CreateEditEstimateNavEnum.CUSTOMER,
  setSelectedEstimateTab: null
});

interface CreateEstimateProps {
  onPreview: () => void;
  onCreateAndShare: () => void;
  isSaveInProgress: boolean;
  isEditEstimateInProgress: boolean;
  setIsEditEstimateInProgress: (value: boolean) => void;
}

const CreateEstimate: FC<CreateEstimateProps> = ({
  onPreview,
  onCreateAndShare,
  isSaveInProgress,
  isEditEstimateInProgress,
  setIsEditEstimateInProgress
}) => {
  const { estimateId } = useParams<{ estimateId: string }>();
  const location = useLocation();
  const isEdit = location.pathname.includes('/edit');
  const isDuplicate = location.pathname.includes('/duplicate');

  const queryParameters = new URLSearchParams(location.search);
  const invoiceId = queryParameters.get('invoiceId');
  const jobId = queryParameters.get('jobId');
  const customerId = queryParameters.get('customerId');

  const [selectedEstimateTab, setSelectedEstimateTab] = useState<CreateEditEstimateNavEnum>(
    CreateEditEstimateNavEnum.CUSTOMER
  );

  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const previousExpiryDateRef = useRef<string | null>(null);

  const dispatch = useAppDispatch();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const isInitialisingEstimateFromAPI = useAppSelector(selectIsInitialisingEstimateFromAPI);
  const reminders = useAppSelector(selectEstimateReminders);
  const remindersEnabled = useAppSelector(selectRemindersEnabled);
  const expiryDate = useAppSelector(selectExpiryDate);
  const {
    data: merchantCustomisations,
    isSuccess: merchantCustomisationsSuccess,
    isFetching: isFetchingMerchantCustomisations
  } = useGetMerchantCustomizationsQuery({
    teamId: currentTeam.id.toString()
  });

  const { isLoading: isLoadingTaxesAndFees } = useGetTaxesAndFeesQuery({
    teamId: currentTeam?.id
  });

  const [getEstimateById] = useLazyGetEstimateByIdQuery();
  const [getJobById] = useLazyGetJobByIdQuery();
  const [getInvoiceById] = useLazyGetInvoiceByIdV2Query();
  const [getCustomerByIdQuery, { isFetching: isFetchingCustomer }] = useLazyGetCustomerByIdQuery();
  const [getEstimateSettings] = useLazyGetEstimateSettingsQuery();

  useEffect(() => {
    if (merchantCustomisationsSuccess) {
      dispatch(updateCustomizations(merchantCustomisations));
      dispatch(
        updateIsSignatureEnabled(
          merchantCustomisations.customerSignatureConfig === FeatureToggleState.ALWAYS_ON ||
            merchantCustomisations.customerSignatureConfig === FeatureToggleState.ON_BY_DEFAULT
        )
      );
    }
  }, [merchantCustomisationsSuccess, dispatch]);

  const getCustomerById = async () => {
    const response = await getCustomerByIdQuery({ teamId: currentTeam.id, customerId });
    dispatch(
      updateEstimateCustomer({
        ...response.data,
        ID: response.data.id,
        billingAddress: response.data.billingAddress as CustomerAddress,
        serviceAddress: response.data.serviceAddress as CustomerAddress[]
      })
    );
    dispatch(updateServiceAddress(response.data?.serviceAddress[0] as CustomerAddress));
    dispatch(updateBillingAddress(response.data?.billingAddress as CustomerAddress));
  };

  useEffect(() => {
    if (customerId) {
      getCustomerById();
    }
  }, [customerId]);

  useEffect(() => {
    if ((isEdit || isDuplicate) && estimateId && !isEditEstimateInProgress) {
      getEstimateById({ teamId: currentTeam.id, estimateId, isFromEdit: true });
      setIsEditEstimateInProgress(true);
    }
  }, [isEdit, estimateId, currentTeam.id, isEditEstimateInProgress]);

  useEffect(() => {
    if (jobId) {
      getJobById({ jobId, teamId: currentTeam.id, isFromEstimate: true });
    }
  }, [jobId, currentTeam.id]);

  useEffect(() => {
    if (invoiceId) {
      getInvoiceById({ invoiceId, teamId: currentTeam.id, isFromEstimate: true });
    }
  }, [invoiceId, currentTeam.id]);

  useEffect(() => {
    const tabElem = document.getElementById(selectedEstimateTab);
    if (tabElem) {
      tabElem.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
    }
  }, [selectedEstimateTab]);

  // Fetch estimate settings on mount for both new and edit modes
  useEffect(() => {
    if (currentTeam.id) {
      getEstimateSettings({ teamId: currentTeam.id }).then(response => {
        if (response.data) {
          const defaultReminders = transformSettingApiToComponentReminders(response.data.data.reminders || [], false);
          const settingsRemindersEnabled = response.data.data.remindersEnabled || false;

          // Store default reminders and settings config in Redux (for both new and edit modes)
          dispatch(
            updateDefaultRemindersFromSettings({
              reminders: defaultReminders,
              enabled: settingsRemindersEnabled
            })
          );

          // For new estimates only: set default reminders, enabled state, and expiry date
          if (!isEdit && !isDuplicate) {
            dispatch(updateEstimateReminders(defaultReminders));
            dispatch(updateRemindersEnabled(settingsRemindersEnabled));

            // Set expiry date based on duration from settings (fallback to 60 days)
            const expiryDuration = response.data.data.expiry?.duration;
            dispatch(updateExpiryDate(getDefaultExpiryDate(DateUtils.getNow(), expiryDuration)));
          }
        }
      });
    }
  }, [currentTeam.id, isEdit, isDuplicate, dispatch]);

  // Watch expiry date changes and recalculate reminder dates
  useEffect(() => {
    if (reminders.length > 0 && expiryDate) {
      const expiryDateString = expiryDate.toISOString();

      // Only recalculate if expiry date has actually changed
      if (previousExpiryDateRef.current !== expiryDateString) {
        previousExpiryDateRef.current = expiryDateString;

        const updatedReminders = reminders.map(reminder => {
          const newReminderDate = calculateScheduledDate(expiryDateString, reminder.scheduleRelation, reminder.days);
          return {
            ...reminder,
            reminderDate: newReminderDate
          };
        });

        dispatch(updateEstimateReminders(updatedReminders));
      }
    }
  }, [expiryDate, reminders, dispatch]);

  const handleSaveReminders = (updatedReminders: ReminderConfig[]) => {
    dispatch(updateEstimateReminders(updatedReminders));
    setIsReminderModalOpen(false);
  };

  const handleTabChange = (tab: CreateEditEstimateNavEnum) => {
    setSelectedEstimateTab(tab);
  };

  return (
    <>
      <CreateEstimateContext.Provider value={{ selectedEstimateTab, setSelectedEstimateTab }}>
        {isInitialisingEstimateFromAPI || isLoadingTaxesAndFees || isFetchingCustomer ? (
          <Loader />
        ) : (
          <div className="relative flex h-full w-full flex-col">
            <CreateEstimateHeader
              isEdit={isEdit}
              onSave={onCreateAndShare}
              isActionInProgress={isSaveInProgress}
              onPreview={onPreview}
            />
            <div className="flex h-full w-full flex-row gap-x-[120px] overflow-hidden bg-primary">
              <div className="hidden w-1/3 max-w-[31.25rem] flex-col items-center p-11 pr-0 lg:flex lg:w-80">
                <CreateEstimateSidebar activeTab={selectedEstimateTab} onTabChange={handleTabChange} />
              </div>
              <div
                id="scrollable-estimate-div"
                className="customNormalScroll flex grow flex-col space-y-8 overflow-y-auto p-12 lg:pl-0 lg:pt-9">
                <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditEstimateNavEnum.CUSTOMER}>
                  <EstimateCustomer />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditEstimateNavEnum.ESTIMATE_DETAILS}>
                  <CreateEstimateDetails context={CreateEstimateContext} />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditEstimateNavEnum.LINE_ITEMS}>
                  <EstimateLineItemSection isLoadingTaxesAndFees={false} context={CreateEstimateContext} />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col" id={CreateEditEstimateNavEnum.NOTES_ATTACHMENTS}>
                  <EstimateNotesAndAttachmentsSection
                    context={CreateEstimateContext}
                    termsAndConditions={merchantCustomisationsSuccess ? merchantCustomisations.estimateTnc : null}
                    isLoadingTermsAndConditions={isFetchingMerchantCustomisations}
                  />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col">
                  <EstimateFormRemindersSection
                    reminders={reminders}
                    isEnabled={remindersEnabled}
                    onToggle={() => dispatch(updateRemindersEnabled(!remindersEnabled))}
                    onOpenModal={() => setIsReminderModalOpen(true)}
                  />
                </div>
                <div className="lg:w-4xl flex max-w-4xl flex-col gap-y-8">
                  <CreateEstimateMoreOptions />
                </div>
              </div>
            </div>
          </div>
        )}
      </CreateEstimateContext.Provider>

      {isReminderModalOpen && (
        <EditReminderModal
          isOpen={isReminderModalOpen}
          onClose={() => setIsReminderModalOpen(false)}
          onSave={handleSaveReminders}
          title="Estimate reminders"
          subtitle="Reminders will be sent to customer's email"
          referenceDate={expiryDate?.toISOString() || ''}
          initialReminders={reminders}
          dateType={ReminderDateTypeLabel.EXPIRY_DATE}
        />
      )}
    </>
  );
};

export default CreateEstimate;
